import React, { useState } from 'react'
import { PlusIcon, XCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

function emptyType() {
  return {
    id: '',
    label: '',
    detail: '',
    workflow: 'activity',
    capture: { showEgm: true, showPhotos: true, showRemark: true },
    options: [{ id: '', label: '' }],
  }
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      {label}
    </label>
  )
}

export default function UarCatalogEditor({ catalog, onChange }) {
  const items = Array.isArray(catalog) ? catalog : []
  const [expanded, setExpanded] = useState(() => new Set(items.slice(0, 2).map((t) => t.id)))

  const updateType = (index, patch) => {
    const next = items.map((t, i) => (i === index ? { ...t, ...patch } : t))
    onChange(next)
  }

  const updateOption = (typeIndex, optionIndex, patch) => {
    const type = items[typeIndex]
    const options = (type.options || []).map((o, i) => (i === optionIndex ? { ...o, ...patch } : o))
    updateType(typeIndex, { options })
  }

  const addType = () => {
    onChange([...items, emptyType()])
  }

  const removeType = (index) => {
    onChange(items.filter((_, i) => i !== index))
  }

  const addOption = (typeIndex) => {
    const type = items[typeIndex]
    updateType(typeIndex, {
      options: [...(type.options || []), { id: '', label: '' }],
    })
  }

  const removeOption = (typeIndex, optionIndex) => {
    const type = items[typeIndex]
    updateType(typeIndex, {
      options: (type.options || []).filter((_, i) => i !== optionIndex),
    })
  }

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Configure report categories and selectable options shown on floor tablets. Staff pick a type and option; an
        optional remark can be added on the device.
      </p>
      {items.map((type, typeIndex) => {
        const open = expanded.has(type.id || `idx-${typeIndex}`)
        const isNil = type.workflow === 'nil' || type.id === 'no_incident'
        return (
          <div key={type.id || `type-${typeIndex}`} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpanded(type.id || `idx-${typeIndex}`)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 text-left hover:bg-gray-100"
            >
              {open ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
              <span className="font-medium text-gray-900">{type.label || '(untitled type)'}</span>
              <span className="text-xs font-mono text-gray-500">{type.id}</span>
              {isNil && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">Nil</span>}
            </button>
            {open && (
              <div className="p-4 space-y-3 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Type ID</label>
                    <input
                      className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5 font-mono"
                      value={type.id || ''}
                      onChange={(e) => updateType(typeIndex, { id: e.target.value.trim().toLowerCase().replace(/\s+/g, '_') })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Workflow</label>
                    <select
                      className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                      value={type.workflow || 'activity'}
                      onChange={(e) => updateType(typeIndex, { workflow: e.target.value })}
                    >
                      <option value="activity">Activity report</option>
                      <option value="nil">Nil attestation</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Label</label>
                    <input
                      className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                      value={type.label || ''}
                      onChange={(e) => updateType(typeIndex, { label: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Subtitle / detail</label>
                    <input
                      className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                      value={type.detail || ''}
                      onChange={(e) => updateType(typeIndex, { detail: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 pt-1">
                  <Toggle
                    label="Show EGM field"
                    checked={type.capture?.showEgm !== false}
                    onChange={(v) => updateType(typeIndex, { capture: { ...type.capture, showEgm: v } })}
                  />
                  <Toggle
                    label="Show photos"
                    checked={type.capture?.showPhotos !== false}
                    onChange={(v) => updateType(typeIndex, { capture: { ...type.capture, showPhotos: v } })}
                  />
                  <Toggle
                    label="Show remarks"
                    checked={type.capture?.showRemark !== false}
                    onChange={(v) => updateType(typeIndex, { capture: { ...type.capture, showRemark: v } })}
                  />
                </div>
                {!isNil && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Options</div>
                    {(type.options || []).map((opt, optionIndex) => (
                      <div key={optionIndex} className="flex flex-wrap gap-2 items-start">
                        <input
                          className="w-36 text-xs border border-gray-300 rounded px-2 py-1.5 font-mono"
                          placeholder="option_id"
                          value={opt.id || ''}
                          onChange={(e) =>
                            updateOption(typeIndex, optionIndex, {
                              id: e.target.value.trim().toLowerCase().replace(/\s+/g, '_'),
                            })
                          }
                        />
                        <input
                          className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded px-2 py-1.5"
                          placeholder="Option label shown on tablet"
                          value={opt.label || ''}
                          onChange={(e) => updateOption(typeIndex, optionIndex, { label: e.target.value })}
                        />
                        <select
                          className="text-xs border border-gray-300 rounded px-2 py-1.5"
                          value={opt.severity || ''}
                          onChange={(e) =>
                            updateOption(typeIndex, optionIndex, {
                              severity: e.target.value || undefined,
                            })
                          }
                        >
                          <option value="">Default severity</option>
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeOption(typeIndex, optionIndex)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addOption(typeIndex)}
                      className="text-xs text-teal-700 hover:text-teal-900 inline-flex items-center gap-1"
                    >
                      <PlusIcon className="w-3.5 h-3.5" /> Add option
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeType(typeIndex)}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Remove type
                </button>
              </div>
            )}
          </div>
        )
      })}
      <button
        type="button"
        onClick={addType}
        className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-900"
      >
        <PlusIcon className="w-4 h-4" /> Add report type
      </button>
    </div>
  )
}
